import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/**
 * MediCare+ buttons.
 *
 * Flat fills, no gradients and no press-scale: the system's character is
 * "sterile-clean", and a transform on press also shrinks the hit area at the
 * exact moment a finger is landing on it. State is carried by background
 * colour alone, which is the one channel that survives a glance across a ward.
 *
 * Every size clears the 44px touch target the spec requires for bedside
 * tablets, except `sm`, which is 36px and is only for dense toolbars where a
 * mouse is the assumed input.
 */
const buttonVariants = cva(
  [
    "inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap",
    "rounded-lg font-semibold transition-colors duration-150",
    "focus-ring",
    // Explicit disabled colours rather than opacity: a half-faded control over
    // an unknown background has an unknown contrast ratio.
    "disabled:pointer-events-none disabled:border-transparent",
    "disabled:bg-disabled disabled:text-disabled-foreground",
    "aria-invalid:error-ring aria-invalid:border-destructive",
    "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  ],
  {
    defaultVariants: {
      size: "default",
      variant: "default",
    },
    variants: {
      size: {
        icon: "size-11 rounded-lg",
        lg: "h-13 px-8 text-base has-[>svg]:px-7",
        default: "h-11 px-6 text-[15px] has-[>svg]:px-5",
        sm: "h-9 gap-1.5 px-4 text-[13px] has-[>svg]:px-3",
      },
      variant: {
        ghost: "text-primary hover:bg-secondary active:bg-accent-active",
        link: "text-primary underline-offset-4 hover:underline hover:text-primary-hover",
        // Red by universal convention, and deliberately not the error red so a
        // recording control is never mistaken for an alert.
        record:
          "bg-record text-record-foreground hover:bg-record/90 active:bg-record/80",
        default:
          "bg-primary text-primary-foreground hover:bg-primary-hover active:bg-primary-active",
        // The quiet filled secondary — same palette as `outline`, no border, for
        // places where a border would add a line the layout does not need.
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-accent-active active:bg-accent-active",
        // The spec's Secondary button. `outline` is what this codebase reaches
        // for by default for a non-primary action, so it gets the treatment the
        // spec names rather than a neutral grey one.
        outline:
          "border-[1.5px] border-primary bg-card text-primary hover:bg-secondary active:bg-accent-active",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80",
      },
    },
  }
)

function Button({
  size,
  variant,
  className,
  type = "button",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot : "button"

  return (
    <Comp
      type={type}
      data-slot="button"
      className={cn(buttonVariants({ size, variant, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
